import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, KeyRound, SlidersHorizontal, Save } from "lucide-react";
import { api, DISTANCES } from "@/lib/handball";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const FIELD_META = [
  { key: "zone", label: "Zon", hint: "V1-H1 · varifrån skottet togs" },
  { key: "distance", label: "Avstånd", hint: "Välj vilka avstånd som ska loggas" },
  { key: "placement", label: "Skottplacering", hint: "Rutnätet 1–9 (och missar 10–15 om skytt loggas)" },
  { key: "shooter", label: "Skytt", hint: "Skyttens tröjnummer 1–99" },
  { key: "contact", label: "Kontakt vid skott", hint: "Ja/Nej – markera om målvakten fick kontakt med bollen" },
  { key: "lob", label: "Lobb", hint: "Ja/Nej – markera om skottet var en lobb" },
];

export default function ProfilePage() {
  const { user, updateStatsConfig } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [nxt, setNxt] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const [cfg, setCfg] = useState(
    user?.stats_config || {
      zone: true,
      distance: true,
      distances: ["9m", "6m", "7m"],
      placement: true,
      shooter: true,
      contact: false,
      lob: false,
    }
  );
  const [cfgSaving, setCfgSaving] = useState(false);
  const cfgDirty = user?.stats_config
    ? FIELD_META.some((f) => cfg[f.key] !== user.stats_config[f.key]) ||
      JSON.stringify([...(cfg.distances || [])].sort()) !==
        JSON.stringify([...(user.stats_config.distances || [])].sort())
    : false;

  const toggleField = (key) => setCfg((c) => ({ ...c, [key]: !c[key] }));

  const toggleDistance = (d) =>
    setCfg((c) => {
      const list = c.distances || [];
      return {
        ...c,
        distances: list.includes(d) ? list.filter((x) => x !== d) : [...list, d],
      };
    });

  const saveConfig = async () => {
    setCfgSaving(true);
    try {
      await updateStatsConfig(cfg);
      toast.success("Statistik-inställningarna är sparade");
    } catch {
      toast.error("Kunde inte spara inställningarna");
    } finally {
      setCfgSaving(false);
    }
  };

  const submit = async () => {
    if (nxt.length < 6) {
      toast.error("Nytt lösenord måste vara minst 6 tecken");
      return;
    }
    if (nxt !== confirm) {
      toast.error("Lösenorden stämmer inte");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: nxt });
      toast.success("Lösenord uppdaterat");
      setCurrent("");
      setNxt("");
      setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kunde inte byta lösenord");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader subtitle="Profil" backTo="/" backLabel="Startsida" />

      <main className="max-w-[720px] mx-auto px-6 py-10 space-y-6">
        <section className="card-tactical p-6" data-testid="profile-user-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            Inloggad som
          </div>
          <div
            className="font-display font-black uppercase tracking-widest text-2xl text-white"
            data-testid="profile-email"
          >
            {user?.email}
          </div>
        </section>

        <section className="card-tactical p-6 space-y-4" data-testid="stats-setup-card">
          <div className="section-hdr">
            <span className="marker" />
            <SlidersHorizontal size={14} className="mr-1" />
            Statistik set up
          </div>
          <div className="text-xs text-zinc-500 leading-relaxed">
            Välj vilka fält som ska vara med när du loggar ett skott. Räddning
            (ja/nej) är alltid obligatoriskt. Övriga fält kan du stänga av om
            du vill logga snabbare.
          </div>

          <div className="space-y-2">
            <div
              className="flex items-center justify-between px-4 py-3 border border-[#27272A] bg-[#0A0A0A] opacity-60"
              data-testid="stats-setup-locked-save"
            >
              <div>
                <div className="font-display uppercase tracking-widest text-sm text-white">
                  Räddning ja/nej
                </div>
                <div className="text-[11px] text-zinc-500">
                  Grunden i all statistik – kan inte stängas av
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-[#34C759] font-display flex items-center gap-1">
                <Lock size={11} /> Låst på
              </span>
            </div>

            {FIELD_META.map((f) => (
              <React.Fragment key={f.key}>
                <label
                  className="flex items-center justify-between gap-3 px-4 py-3 border border-[#27272A] bg-[#0A0A0A] cursor-pointer hover:bg-[#111]"
                  data-testid={`stats-setup-row-${f.key}`}
                >
                  <div>
                    <div className="font-display uppercase tracking-widest text-sm text-white">
                      {f.label}
                    </div>
                    <div className="text-[11px] text-zinc-500">{f.hint}</div>
                  </div>
                  <Checkbox
                    data-testid={`stats-setup-checkbox-${f.key}`}
                    checked={cfg[f.key]}
                    onCheckedChange={() => toggleField(f.key)}
                    className="border-[#3F3F46] data-[state=checked]:bg-[#34C759] data-[state=checked]:text-black rounded-none w-5 h-5"
                  />
                </label>

                {f.key === "distance" && cfg.distance && (
                  <div
                    className="ml-6 pl-4 border-l-2 border-[#27272A] space-y-1.5 pb-2"
                    data-testid="stats-setup-distances-group"
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display pb-1">
                      Inkludera avstånd
                    </div>
                    {DISTANCES.map((d) => {
                      const active = (cfg.distances || []).includes(d.value);
                      return (
                        <label
                          key={d.value}
                          className="flex items-center justify-between gap-3 px-3 py-2 border border-[#171717] bg-[#050505] cursor-pointer hover:bg-[#0d0d0d]"
                          data-testid={`stats-setup-distance-${d.value}`}
                        >
                          <span className="font-display uppercase tracking-widest text-xs text-white">
                            {d.label}
                          </span>
                          <Checkbox
                            data-testid={`stats-setup-distance-checkbox-${d.value}`}
                            checked={active}
                            onCheckedChange={() => toggleDistance(d.value)}
                            className="border-[#3F3F46] data-[state=checked]:bg-[#34C759] data-[state=checked]:text-black rounded-none w-4 h-4"
                          />
                        </label>
                      );
                    })}
                    {(cfg.distances || []).length === 0 && (
                      <div className="text-[11px] text-[#FF3B30] pt-1">
                        Välj minst ett avstånd
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <Button
            data-testid="save-stats-setup-btn"
            onClick={saveConfig}
            disabled={cfgSaving || !cfgDirty || (cfg.distance && (cfg.distances || []).length === 0)}
            className="w-full h-12 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest disabled:opacity-40"
          >
            <Save size={16} className="mr-2" />
            {cfgDirty ? "Spara statistik-inställningar" : "Inställningar sparade"}
          </Button>
        </section>

        <section className="card-tactical p-6 space-y-4" data-testid="change-password-card">
          <div className="section-hdr">
            <span className="marker" />
            Byt lösenord
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Nuvarande lösenord
            </Label>
            <Input
              data-testid="current-password-input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="bg-black border-[#3F3F46] text-white h-12 focus-visible:ring-[#34C759]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Nytt lösenord
            </Label>
            <Input
              data-testid="new-password-input"
              type="password"
              value={nxt}
              onChange={(e) => setNxt(e.target.value)}
              autoComplete="new-password"
              className="bg-black border-[#3F3F46] text-white h-12 focus-visible:ring-[#34C759]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Bekräfta nytt lösenord
            </Label>
            <Input
              data-testid="confirm-password-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="bg-black border-[#3F3F46] text-white h-12 focus-visible:ring-[#34C759]"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
              <Lock size={11} />
              Minst 6 tecken
            </div>
          </div>

          <Button
            data-testid="save-password-btn"
            onClick={submit}
            disabled={saving || !current || !nxt || !confirm}
            className="w-full h-12 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest disabled:opacity-40"
          >
            <KeyRound size={16} className="mr-2" />
            Spara nytt lösenord
          </Button>
        </section>
      </main>
    </div>
  );
}
